/**
 * Desktop nav dropdowns.
 *
 * Hover alone is not enough: a bare mouseleave closes the panel the instant
 * the pointer crosses the gap below the link, and it locks out keyboard and
 * screen-reader users entirely. This adds a close delay, full keyboard
 * support, and viewport-edge flipping.
 *
 * The bar is hidden below 750px and on coarse pointers, where Horizon's own
 * drawer is the menu, so nothing here runs on mobile.
 */

/** Grace period before a hovered-away panel closes, ms. */
const CLOSE_DELAY = 220;

const items = [...document.querySelectorAll('.phs-nav__item--has')];

/** @param {HTMLElement} item @param {boolean} open */
function setOpen(item, open) {
  item.classList.toggle('is-open', open);
  item.querySelector('.phs-nav__link')?.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (open) keepInViewport(item);
}

function closeAll(except) {
  items.forEach((i) => {
    if (i !== except) setOpen(i, false);
  });
}

/** Flip a panel to right-aligned if left-aligned would overflow the window. */
function keepInViewport(item) {
  const panel = item.querySelector('[data-phs-drop]');
  if (!panel) return;
  panel.classList.remove('is-flipped');
  const box = panel.getBoundingClientRect();
  if (box.right > document.documentElement.clientWidth - 8) {
    panel.classList.add('is-flipped');
  }
}

items.forEach((item) => {
  const link = item.querySelector('.phs-nav__link');
  const panel = item.querySelector('[data-phs-drop]');
  if (!link || !panel) return;

  let closeTimer = 0;
  const cancelClose = () => clearTimeout(closeTimer);
  const scheduleClose = () => {
    cancelClose();
    closeTimer = window.setTimeout(() => setOpen(item, false), CLOSE_DELAY);
  };

  item.addEventListener('pointerenter', () => {
    cancelClose();
    closeAll(item);
    setOpen(item, true);
  });
  item.addEventListener('pointerleave', scheduleClose);

  // Focus opens it for keyboard users; moving focus out of the subtree closes.
  item.addEventListener('focusin', () => {
    cancelClose();
    closeAll(item);
    setOpen(item, true);
  });
  item.addEventListener('focusout', (event) => {
    if (!item.contains(event.relatedTarget)) setOpen(item, false);
  });

  link.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') {
      // Open and land on the first entry rather than following the link.
      event.preventDefault();
      setOpen(item, true);
      panel.querySelector('a')?.focus();
    }
    if (event.key === 'Escape') {
      setOpen(item, false);
      link.blur();
    }
  });

  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    setOpen(item, false);
    link.focus();
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeAll(null);
});

// A panel measured while open can become an overflow once the window narrows.
let resizeSettle = 0;
window.addEventListener('resize', () => {
  clearTimeout(resizeSettle);
  resizeSettle = window.setTimeout(() => {
    items.filter((i) => i.classList.contains('is-open')).forEach(keepInViewport);
  }, 120);
});
